import { useCallback, useEffect, useRef } from 'react';

function normalizeSessionId(value) {
  return String(value || '').trim();
}

export function shouldResetWorkspaceForSessionTransition({
  hasObservedSession = false,
  nextSessionId = '',
  previousSessionId = '',
} = {}) {
  const previous = normalizeSessionId(previousSessionId);
  const next = normalizeSessionId(nextSessionId);
  if (!hasObservedSession || previous === next) {
    return false;
  }
  if (!next) {
    return true;
  }
  return Boolean(previous);
}

export function useSessionWorkspaceReset({
  activeSessionId = '',
  resetWorkspace,
} = {}) {
  const previousSessionIdRef = useRef('');
  const hasObservedSessionRef = useRef(false);

  useEffect(() => {
    const nextSessionId = normalizeSessionId(activeSessionId);
    const shouldReset = shouldResetWorkspaceForSessionTransition({
      hasObservedSession: hasObservedSessionRef.current,
      nextSessionId,
      previousSessionId: previousSessionIdRef.current,
    });
    previousSessionIdRef.current = nextSessionId;
    hasObservedSessionRef.current = true;
    if (shouldReset) {
      resetWorkspace?.();
    }
  }, [activeSessionId, resetWorkspace]);
}

export function useControllerSessionWorkspaceReset({
  sidebarNavigation = null,
  activeSessionId = '',
  setActiveWorkspaceTab,
  setCanvasActiveTabKey,
  setCanvasFullscreenOpen,
  setKnowledgeHubPopoverOpen,
  setShowCanvasPanel,
} = {}) {
  const requestId = sidebarNavigation?.requestId;
  const scopeKey = sidebarNavigation?.scopeKey;
  useEffect(() => {
    if (requestId) setActiveWorkspaceTab?.('chats');
  }, [requestId, scopeKey, setActiveWorkspaceTab]);
  const resetWorkspace = useCallback(() => {
    setActiveWorkspaceTab?.('chats');
    setKnowledgeHubPopoverOpen?.(false);
    setShowCanvasPanel?.(false);
    setCanvasActiveTabKey?.('');
    setCanvasFullscreenOpen?.(false);
  }, [
    setActiveWorkspaceTab,
    setCanvasActiveTabKey,
    setCanvasFullscreenOpen,
    setKnowledgeHubPopoverOpen,
    setShowCanvasPanel,
  ]);

  useSessionWorkspaceReset({
    activeSessionId,
    resetWorkspace,
  });
}

export default useSessionWorkspaceReset;
